<template>
  <div class="my-account-login">
    <consent-dialog-fi
      ref="consentDialogRef"
      :fields="fields"
      @confirm="redirectTo(successRedirect)"
    />

    <loading-indicator
      v-if="isHandlingCode"
      variant="spinnerFullPageCover"
    />
    <component-wrapper v-else-if="autodetect === true">
      <country-selection-list-fi
        only-emit-on-click
        @country-clicked="countryClicked"
      />
    </component-wrapper>
    <site-login-standalone
      v-else
      :autodetect
      @logged-in="loggedInStandalone"
    />
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@urql/vue';
import { LoadingIndicator } from 'atoms';
import { onMounted, ref, watchEffect } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import CountrySelectionListFi from '@/components/country-selection-list-fi/CountrySelectionListFi.vue';
import { getServices } from '@/components/fixperience/getLink';
import ConsentDialogFi from '@/components/my-account/site-login/consent-dialog-fi/ConsentDialogFi.vue';
import SiteLoginStandalone from '@/components/my-account/site-login/SiteLoginStandalone.vue';
import { type ConsentTemplateWithRequired, useConsents } from '@/composables/consents';
import { type LocaleCountry, useCountryList } from '@/composables/countryList';
import { useFields } from '@/composables/fields';
import { useFischer } from '@/composables/fischerPlugin';
import { useLogin } from '@/composables/login';
import { useTokenRefresh } from '@/composables/tokenRefresh';
import GIVECONSENT from '@/graphql/giveConsent.gql';
import { postFormEncoded } from '@/lib/postFormEncoded';

interface LoggedInEvent {
  accessToken: string;
  redirect: string;
  data?: string;
}

interface Country {
  Value: string;
  Localizations?: Array<{
    Iso_Code?: string;
    Language?: {
      Name?: string;
    };
  }>;
}

interface UserLocale {
  Value: string;
  Language?: {
    Name?: string;
  };
}

const fields = useFields('MyAccountLoginFields');

const fischer = useFischer();
const {
  checkIsValidRedirect,
  getCookies,
  logDebug,
  postAccessTokenInSession,
  sendLoginNotification,
  setCookies,
  setDevToken,
} = useLogin();
const { countryList, loadCountryList, countryListLoaded } = useCountryList();
const { getConsentTemplates } = useConsents();
const { scheduleRefresh } = useTokenRefresh();

const consentDialogRef = ref<InstanceType<typeof ConsentDialogFi> | null>(null);

const isHandlingCode = ref(!!(fischer.query('code') && fischer.query('state')));
const isFixperience =
  fischer.settings()?.shop?.clientIdOverwrite === 'fiwe-zz-fixperience' ||
  (IS_SHOWROOM && window.location.pathname.startsWith('/fixperience'));

const autodetect = ref<string | boolean>(isFixperience && !isHandlingCode.value);
const successRedirect = ref<string>('');
const newConsentTemplates = ref<Array<{ id: string; version: number }>>([]);

const { executeMutation: giveConsentMutation } = useMutation(GIVECONSENT);

function isCn(): boolean {
  return fischer.siteLanguage() === 'zh-CN';
}

function countryClicked(_country: any, locale?: LocaleCountry): void {
  autodetect.value = locale?.iso_Code || false;
}

function isValidRedirect(url: string): boolean;
function isValidRedirect(url: undefined): false;
function isValidRedirect(url: string | undefined): url is string;
function isValidRedirect(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  if (
    url.startsWith('/') ||
    url === window.location.origin ||
    url.startsWith(`${window.location.origin}/`) ||
    url.startsWith(`${window.location.origin}#`)
  ) {
    return true;
  }

  if (!isFixperience) {
    return false;
  }
  if (IS_SHOWROOM) {
    return true;
  }
  if (APP_ENVIRONMENT === 'UAT') {
    if (
      /^https?:\/\/(?:localhost:[0-9]|[a-zA-Z0-9_.-]+\.fixperience\.online)(?:\/|$|#)/.test(url)
    ) {
      return true;
    }
  }
  const services = getServices(isCn());
  return Object.values(services).some(
    (serviceUrl) =>
      url.startsWith(`${serviceUrl}#`) || url.startsWith(`${serviceUrl}/`) || url === serviceUrl,
  );
}

function redirectTo(url: string | undefined): void {
  if (isValidRedirect(url)) {
    window.location.href = url;
  } else {
    console.error('could not redirect', url);
    window.location.href = '/';
  }
}

async function acceptConsent(accessToken: string): Promise<void> {
  await Promise.all(
    newConsentTemplates.value.map(async (consentTemplate) => {
      const { error: consentError } = await giveConsentMutation({
        baseSiteId: fischer.baseSiteId(),
        accessToken,
        userId: 'current',
        consentTemplateId: consentTemplate.id,
        consentTemplateVersion: consentTemplate.version,
        lang: fischer.language_639_1(),
      });
      if (consentError) {
        console.error('could not record consent', consentError);
      }
    }),
  );
}

async function checkNewConsents(accessToken: string): Promise<boolean> {
  if (!fields.enableConsentDialog?.value) {
    return false;
  }

  try {
    const userConsentTemplates = await getConsentTemplates(accessToken);
    if (!userConsentTemplates) {
      return false;
    }

    const registrationConsents = userConsentTemplates.filter(
      (consent): consent is ConsentTemplateWithRequired =>
        consent !== null &&
        (consent.id === `REGISTRATION_DATA_PRIVACY_${fischer.baseSiteId()}` ||
          consent.id === `REGISTRATION_TERMS_AND_CONDITIONS_${fischer.baseSiteId()}`),
    );

    let newConsent = false;
    registrationConsents.forEach((consent) => {
      if (!consent.currentConsent) {
        newConsent = true;
        newConsentTemplates.value.push({ id: consent.id, version: consent.version });
      }
    });

    if (newConsent) {
      consentDialogRef.value?.open();
      acceptConsent(accessToken);
    }

    return newConsent;
  } catch (error) {
    console.error(error);
    return false;
  }
}

async function loggedInStandalone(el: LoggedInEvent): Promise<void> {
  const newConsent = await checkNewConsents(el.accessToken);
  let { redirect } = el;
  const separator = redirect.includes('#') ? '&' : '#';
  if (el.data) {
    redirect = `${redirect}${separator}${el.data}&logged-in=true`;
  } else {
    redirect = `${redirect}${separator}logged-in=true`;
  }

  if (newConsent) {
    successRedirect.value = redirect;
  } else {
    redirectTo(redirect);
  }
}

const manualLanguage = fischer.query('manual-language') === 'true';
const isLogin =
  !IS_SSR && (/[#&]access_token=/.test(window.location.hash) || fischer.query('code'));

if (manualLanguage) {
  autodetect.value = fischer.languageIsoCode();
} else if (autodetect.value && !IS_SSR && !isLogin) {
  if (!IS_SSR) {
    loadCountryList();
  }

  watchEffect(() => {
    if (!countryListLoaded.value) {
      return;
    }

    const list = countryList.value?.countryList as Country[] | undefined;
    const userLocale = countryList.value?.user as UserLocale | undefined;
    if (!list?.length) {
      if (!IS_SSR) {
        (window as any).countryList = countryList.value;
      }
      return;
    }

    const detectedCountry =
      userLocale && list.find((country) => country.Value === userLocale.Value);

    if (userLocale) {
      const detectedLocale = detectedCountry?.Localizations?.find(
        (locale) => locale.Language?.Name === userLocale.Language?.Name,
      );
      const detectedIso = detectedLocale?.Iso_Code;
      if (!detectedCountry || !detectedIso) {
        console.error(
          'could not find country/locale for detected locale',
          JSON.stringify(userLocale),
        );
        return;
      }

      autodetect.value = detectedIso;
    }
  });
}

const resolveUrl = (url: string): string => {
  const resolved = new URL(url, window.location.origin);

  return `${window.location.origin}${resolved.pathname}${resolved.search}${resolved.hash}`;
};

async function handleCode(code: string, state: any): Promise<void> {
  const redirectUri = resolveUrl(fischer.pages.login().url);

  const response = await postFormEncoded(
    `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/token`,
    {
      grant_type: 'authorization_code',
      client_id: fischer.getClientId(),
      code,
      code_verifier: state.verifier,
      redirect_uri: redirectUri,
    },
  );
  const json = await response.json();
  const accessToken = json.access_token;
  const idToken = json.id_token;
  const expiresIn = json.expires_in;
  const refreshToken = json.refresh_token;
  if (!accessToken || !idToken || !expiresIn) {
    throw new Error('Missing tokens in response');
  }

  if (refreshToken) {
    if (IS_SHOWROOM) {
      // The refresh token cookie is showroom-only; in production the token
      // is stored in the Sitecore session via the Login action below
      fischer.user.setRefreshToken(refreshToken);
    }
    scheduleRefresh(Number(expiresIn));
  }

  let loginSuccessPage: string;
  if (IS_SHOWROOM) {
    setDevToken(accessToken, idToken);
    loginSuccessPage = '/';
  } else {
    try {
      logDebug('posting token to sitecore');
      loginSuccessPage =
        (await postAccessTokenInSession(accessToken, idToken, refreshToken)) || '/';
      await sendLoginNotification(accessToken, idToken);
    } catch {
      logDebug('error occured when posting to sitecore, not redirecting');
      isHandlingCode.value = false;
      return;
    }
  }

  if (state.query) {
    loginSuccessPage = `${loginSuccessPage}${state.query}`;
  }
  if (state.hash) {
    loginSuccessPage = `${loginSuccessPage}${state.hash}`;
  }

  const redirectFromState = checkIsValidRedirect(state?.redirect);
  const redirectFromSitecore = checkIsValidRedirect(loginSuccessPage);
  const validRedirect = redirectFromState || redirectFromSitecore || '/';

  logDebug('logged in');
  window.location.href = `${validRedirect}${validRedirect.includes('#') ? '&' : '#'}logged-in=true`;
}

onMounted(() => {
  const code = fischer.query('code');
  const stateId = fischer.query('state');
  if (code && stateId) {
    const cookies = getCookies();
    const state = cookies?.[stateId];
    if (!state) {
      console.error('Could not find state for login');
      window.location.href = '/';
      return;
    }
    delete cookies[stateId];
    setCookies(cookies).then(() => {
      handleCode(code, state);
    });
    return;
  }

  if (isFixperience && isLogin) {
    autodetect.value = fischer.languageIsoCode();
  }

  // Store redirect parameter in session storage
  const redirect = fischer.query('redirect');
  if (redirect) {
    try {
      sessionStorage.setItem('login-redirect', redirect);
    } catch (error) {
      console.error(error);
    }
  }

  if (IS_SSR) {
    return;
  }

  // login will fail (CORS policy) if we are on an unsecure connection
  if (window.location.protocol !== 'https:' && !IS_SHOWROOM) {
    window.location.replace(
      `https:${window.location.href.substring(window.location.protocol.length)}`,
    );
    return;
  }

  // Handle authenticated user redirect
  if (!redirect) {
    return;
  }
  if (fischer.user.authenticated()) {
    redirectTo(redirect);
  }
});
</script>

<style scoped src="./my-account-login" />
